import { Modal } from '@/modules/common/components/Modal';
import {
  ApplicationValidationStatus,
  ApplicationValidationType,
} from '@/types/application';
import { Check, Close } from '@mui/icons-material';
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import pluralize from 'pluralize';
import { useState } from 'react';
import { ReviewModalProps, ReviewModalState } from './ReviewModal.types';

type PaymentFeaturesReviewModalProps = ReviewModalProps & {
  validationsToReview: ApplicationValidationType[];
};

const MODAL_VALIDATION_TYPES = [
  ApplicationValidationType.FlexibleRemuneration,
  ApplicationValidationType.LearningBudget,
];

export function PaymentFeaturesReviewModal({
  applicationsToReview,
  validationsToReview,
  initialReview,
  onClose,
  ...props
}: PaymentFeaturesReviewModalProps) {
  const [review, setReview] = useState<ReviewModalState>(initialReview);

  const isValid = Object.entries(review)
    .filter(
      ([validationType]) =>
        MODAL_VALIDATION_TYPES.includes(
          validationType as ApplicationValidationType,
        ) &&
        applicationsToReview.some(applicationsToReview =>
          applicationsToReview.validations?.some(
            validation => validation.name === validationType,
          ),
        ),
    )
    .every(([, value]) => value === true || value === false);

  return (
    <Modal
      {...props}
      title={
        <>
          You have selected{' '}
          <strong>{applicationsToReview.length} applications</strong> to approve
        </>
      }
      onClose={() => onClose()}
      showCloseButton
      classes={{
        paper: 'max-w-2xl',
      }}
      actions={
        <>
          <Button
            variant="outlined"
            type="button"
            onClick={() => onClose(undefined)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!isValid}
            onClick={() => {
              onClose(review);
            }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p>
          Selecting 'Approve' will automatically apply tax benefits and learning
          budgets to the selected applications. If you choose 'Reject', users
          will be declined and notified by email with more information.
        </p>
        <div className="flex flex-col gap-4">
          {MODAL_VALIDATION_TYPES.map(validationType => {
            const numberOfApplications = applicationsToReview.filter(
              applicationsToReview =>
                applicationsToReview.validations?.some(
                  validation =>
                    validation.name === validationType &&
                    validation.reviews.some(
                      review =>
                        review.status === ApplicationValidationStatus.Pending,
                    ),
                ),
            ).length;

            if (!numberOfApplications) {
              return null;
            }

            return (
              <div
                key={validationType}
                className="flex flex-row gap-2 items-center justify-between"
              >
                <p>
                  {numberOfApplications}{' '}
                  {pluralize('application', numberOfApplications)} requesting{' '}
                  {validationType ===
                  ApplicationValidationType.FlexibleRemuneration
                    ? ('tax benefits' as const)
                    : ('learning budget' as const)}
                </p>
                <ToggleButtonGroup
                  value={review[validationType]}
                  aria-required={true}
                  exclusive
                  onChange={(_e, value) => {
                    setReview({
                      ...review,
                      [validationType]: value,
                    });
                  }}
                >
                  <Tooltip title="Approve">
                    <ToggleButton color="success" value={true}>
                      <Check />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <ToggleButton color="error" value={false}>
                      <Close />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
